import { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import { createLogger } from '@/lib/logger';
import { supabase } from '@/lib/supabase';
import { useTheme } from '@/contexts/ThemeContext';
import { TrendingUp } from 'lucide-react-native';

const log = createLogger('LIFT_HISTORY');

interface LiftHistoryRow {
  id: string;
  weight: number;
  reps: number;
  performed_at: string;
  weight_unit: string;
}

interface LiftHistoryProps {
  exerciseId: string;
  exerciseName: string;
  profileId: string;
  /** Max rows to show. Default 3. */
  limit?: number;
}

/**
 * Inline component showing recent logged entries for a specific exercise.
 * Queries strength_events by profile + lift_key, sorted by performed_at desc.
 */
export default function LiftHistory({ exerciseId, exerciseName, profileId, limit = 3 }: LiftHistoryProps) {
  const { theme } = useTheme();
  const c = theme.colors;
  const [rows, setRows] = useState<LiftHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        // Normalize exercise name to lift_key format (same as record flow)
        const liftKey = exerciseName.toLowerCase().replace(/[^a-z0-9]+/g, '-');

        const { data, error } = await supabase
          .from('strength_events')
          .select('id, weight, reps, performed_at, weight_unit')
          .eq('profile_id', profileId)
          .eq('lift_key', liftKey)
          .order('performed_at', { ascending: false })
          .limit(limit);

        if (error) {
          log.warn('Failed to fetch lift history:', error.message);
          if (!cancelled) setRows([]);
          return;
        }

        if (!cancelled) setRows((data as LiftHistoryRow[]) ?? []);
      } catch (e: unknown) {
        log.error('Lift history exception:', e instanceof Error ? e.message : e);
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [exerciseId, exerciseName, profileId, limit]);

  if (loading) {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="small" color={c.textTertiary} />
      </View>
    );
  }

  if (rows.length === 0) return null;

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  return (
    <View style={[styles.container, { borderTopColor: c.border }]}>
      <View style={styles.header}>
        <TrendingUp size={12} color={c.textTertiary} />
        <Text style={[styles.headerText, { color: c.textTertiary }]}>Recent</Text>
      </View>
      {rows.map((row) => (
        <View key={row.id} style={styles.row}>
          <Text style={[styles.date, { color: c.textTertiary }]}>{formatDate(row.performed_at)}</Text>
          <Text style={[styles.detail, { color: c.textSecondary }]}>
            {row.weight}{row.weight_unit} × {row.reps}
          </Text>
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginBottom: 4,
  },
  headerText: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 2,
  },
  date: {
    fontSize: 12,
  },
  detail: {
    fontSize: 12,
    fontWeight: '500',
  },
});
